import { Modal, ModalTitle } from "@msys/ui";
import InfoIcon from "@mui/icons-material/InfoOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { cloneDeep, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { ManualSave } from "../../../commons/form-fields/ManualSave.js";
import {
  QuestionControlForm,
  QuestionControlSectionFormValues,
  useQuestionControlValidationSchema,
} from "../QuestionControlSection.js";

interface Props {
  questionControlOverride:
    | QuestionControlSectionFormValues["questionControl"]
    | null
    | undefined;
  questionControlBase:
    | QuestionControlSectionFormValues["questionControl"]
    | null
    | undefined;
  hidePrompt?: boolean;
  handleCancel: () => void;
  handleSubmit: (
    values: QuestionControlSectionFormValues,
    overrideExists: boolean
  ) => void | Promise<void>;
}

function OverrideExistsTracker({
  isResettingRef,
  setOverrideExists,
}: {
  isResettingRef: React.MutableRefObject<boolean>;
  setOverrideExists: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { values } = useFormikContext<QuestionControlSectionFormValues>();
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }
    setOverrideExists(true);
  }, [values, isResettingRef, setOverrideExists]);

  return null;
}

export const QuestionControlFormWithOverride = ({
  questionControlOverride,
  questionControlBase,
  hidePrompt,
  handleCancel,
  handleSubmit,
}: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const hasInitialOverride =
    questionControlOverride !== null && questionControlOverride !== undefined;
  const [overrideExists, setOverrideExists] =
    React.useState(hasInitialOverride);
  const isResettingRef = React.useRef(false);

  const baseValues: QuestionControlSectionFormValues = React.useMemo(
    () => ({
      prompt: "",
      questionControl: questionControlBase ?? [],
    }),
    [questionControlBase]
  );

  const initialValues: QuestionControlSectionFormValues = React.useMemo(
    () =>
      cloneDeep({
        prompt: "",
        questionControl: hasInitialOverride
          ? (questionControlOverride ?? [])
          : (questionControlBase ?? []),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const questionControlSchema = useQuestionControlValidationSchema();
  const validationSchema = React.useMemo(
    () => Yup.object().shape({ ...questionControlSchema }),
    [questionControlSchema]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const onSubmit = async (values: QuestionControlSectionFormValues) => {
    await handleSubmit(values, overrideExists);
  };

  return (
    <Formik<QuestionControlSectionFormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {formikProps => (
        <Form id={formId}>
          <Stack direction={"column"} spacing={1}>
            <ModalTitle
              title={t("Question control", { ns: "QuoteItem" })}
              headerButtons={
                <Tooltip
                  title={
                    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                      {t(
                        "The question control determines in which wizard a question is shown and to whom. You can set up additional configurations and remove existing ones.",
                        { ns: "QuoteItem" }
                      )}
                    </Box>
                  }
                >
                  <InfoIcon />
                </Tooltip>
              }
              headerActions={
                <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
                  <IconButton
                    color="primary"
                    size="small"
                    disabled={!overrideExists}
                    onClick={async () => {
                      isResettingRef.current = true;
                      setOverrideExists(false);
                      await formikProps.setValues(baseValues);
                    }}
                  >
                    <Tooltip title={t("Reset", { ns: "Global" })}>
                      <RestartAltIcon />
                    </Tooltip>
                  </IconButton>
                </RestrictedByOrganisationPermissionWithDebug>
              }
              titleProps={{ sx: { p: "0 !important" }, variant: "h3" }}
            />

            <OverrideExistsTracker
              isResettingRef={isResettingRef}
              setOverrideExists={setOverrideExists}
            />

            <QuestionControlForm hidePrompt={hidePrompt} />

            <ManualSave
              disabled={formikProps.isSubmitting}
              disabledSubmit={!formikProps.isValid || !formikProps.dirty}
              onCancel={handleCancel}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
